import React, { useState } from 'react';
import {
  Box, Dialog, DialogTitle, DialogContent, DialogActions, FormControl, FormControlLabel,
  Grid, IconButton, InputLabel, MenuItem, Select, Switch, TextField, Typography, Button,
  CircularProgress,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import axios from '../../config/axios';
import { getApiUrl } from '../../config/environment';
import { useAuth } from '../../contexts/AuthContext';
import UserAvatarUpload from './UserAvatarUpload';
import UserProfileChangePasswordSection from './UserProfileChangePasswordSection';
import UserProfileTwoFactorSection from './UserProfileTwoFactorSection';
import { clinicSettingsPrimaryButtonSx } from '../../pages/Clinic/clinicSettingsTheme';
import { userHasAgendaSplitEditConsultationLayout } from '../../config/permissions';
import {
  AgendaDefaultDayLayout,
  AgendaDefaultView,
  AgendaProfessionalsDefault,
  readAgendaUserPreferences,
  resolveAgendaProfessionalsMode,
  resolveAgendaSplitEditHoverExpand,
  userCanViewOtherProfessionalsAgenda,
} from '../../utils/agendaUserPreferences';

const DEFAULT_AGENDA_DAY_LAYOUT: AgendaDefaultDayLayout = 'professionals';

type UserGender = 'male' | 'female';

interface AgendaPrefsForm {
  professionals_default: AgendaProfessionalsDefault;
  default_view: AgendaDefaultView;
  day_layout: AgendaDefaultDayLayout;
  split_edit_hover_expand: boolean;
}

function agendaPrefsFormFromUser(user: any): AgendaPrefsForm {
  const prefs = readAgendaUserPreferences(user);
  return {
    professionals_default: resolveAgendaProfessionalsMode(user),
    default_view: prefs.default_view ?? 'last',
    day_layout: prefs.day_layout ?? DEFAULT_AGENDA_DAY_LAYOUT,
    split_edit_hover_expand: resolveAgendaSplitEditHoverExpand(user),
  };
}

export interface UserProfileDialogProps {
  open: boolean;
  onClose: () => void;
}

const UserProfileDialog: React.FC<UserProfileDialogProps> = ({ open, onClose }) => {
  const { user, refreshUser } = useAuth();
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    full_name: user?.full_name || '',
    phone: user?.phone || '',
    gender: (user?.gender || '') as UserGender | '',
  });
  const [agendaForm, setAgendaForm] = useState<AgendaPrefsForm>(() => agendaPrefsFormFromUser(user));

  React.useEffect(() => {
    if (!open || !user) return;
    setForm({
      full_name: user.full_name || '',
      phone: user.phone || '',
      gender: (user.gender || '') as UserGender | '',
    });
    setAgendaForm(agendaPrefsFormFromUser(user));
  }, [open, user?.id, user?.full_name, user?.phone, user?.gender, user?.preferences]);

  if (!user) return null;

  const hasLinkedProfessional = !!user.professional_id;
  const showProfessionalsDefault = userCanViewOtherProfessionalsAgenda(user);
  const showSplitEditHoverPref = userHasAgendaSplitEditConsultationLayout(user);

  const handleSave = async () => {
    if (!form.gender) {
      window.alert('Selecione o gênero para o avatar padrão (caso não use foto).');
      return;
    }
    setSaving(true);
    try {
      await axios.put(`${getApiUrl()}/auth/profile`, {
        full_name: form.full_name.trim(),
        phone: form.phone.trim() || null,
        gender: form.gender,
        preferences: {
          ...(user.preferences || {}),
          agenda: {
            ...readAgendaUserPreferences(user),
            ...(showProfessionalsDefault ? { professionals_default: agendaForm.professionals_default } : {}),
            default_view: agendaForm.default_view,
            day_layout: agendaForm.day_layout,
            ...(showSplitEditHoverPref
              ? { split_edit_hover_expand: agendaForm.split_edit_hover_expand }
              : {}),
          },
        },
      });
      await refreshUser();
      onClose();
    } catch (err: any) {
      window.alert(err.response?.data?.error || 'Erro ao salvar perfil');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth scroll="paper">
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          fontWeight: 700,
          color: '#0f172a',
          pr: 1,
        }}
      >
        Meu perfil
        <IconButton aria-label="Fechar" onClick={onClose} size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" sx={{ color: '#64748b', mb: 2.5 }}>
          Sua foto aparece no chat de equipe, menu lateral e demais áreas do sistema.
        </Typography>
        <Grid container spacing={3} alignItems="flex-start">
          <Grid item xs={12} sm="auto">
            <Box sx={{ display: 'flex', justifyContent: { xs: 'center', sm: 'flex-start' } }}>
              <UserAvatarUpload
                fullName={form.full_name || user.full_name || 'Usuário'}
                avatarUrl={user.avatar_url}
                gender={form.gender || user.gender}
                onAvatarUpdated={async () => { await refreshUser(); }}
              />
            </Box>
          </Grid>
          <Grid item xs={12} sm>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  size="small"
                  label="Nome completo"
                  value={form.full_name}
                  onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Telefone"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Gênero (avatar padrão)</InputLabel>
                  <Select
                    value={form.gender}
                    label="Gênero (avatar padrão)"
                    onChange={(e) => setForm({ ...form, gender: e.target.value as UserGender })}
                  >
                    <MenuItem value="male">Homem</MenuItem>
                    <MenuItem value="female">Mulher</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth size="small" label="E-mail" value={user.email} disabled />
              </Grid>
            </Grid>
          </Grid>
        </Grid>

        <Box sx={{ mt: 3, pt: 2.5, borderTop: '1px solid #e2e8f0' }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0f172a', mb: 0.5 }}>
            Agenda
          </Typography>
          <Typography variant="body2" sx={{ color: '#64748b', mb: 2 }}>
            Como a agenda abre para você. Durante o uso você pode marcar e desmarcar à vontade.
          </Typography>
          <Grid container spacing={2}>
            {showProfessionalsDefault && (
              <Grid item xs={12}>
                <FormControl fullWidth size="small">
                  <InputLabel>Profissionais exibidos ao abrir</InputLabel>
                  <Select
                    value={agendaForm.professionals_default}
                    label="Profissionais exibidos ao abrir"
                    onChange={(e) =>
                      setAgendaForm({
                        ...agendaForm,
                        professionals_default: e.target.value as AgendaProfessionalsDefault,
                      })}
                  >
                    {hasLinkedProfessional && (
                      <MenuItem value="mine">Só a minha agenda (marco os outros quando precisar)</MenuItem>
                    )}
                    <MenuItem value="all">Todos os profissionais (desmarco para filtrar)</MenuItem>
                    <MenuItem value="last">Lembrar a última seleção</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            )}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Visão inicial</InputLabel>
                <Select
                  value={agendaForm.default_view}
                  label="Visão inicial"
                  onChange={(e) =>
                    setAgendaForm({ ...agendaForm, default_view: e.target.value as AgendaDefaultView })}
                >
                  <MenuItem value="last">Última usada</MenuItem>
                  <MenuItem value="day">Dia</MenuItem>
                  <MenuItem value="week">Semana</MenuItem>
                  <MenuItem value="month">Mês</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Layout do dia</InputLabel>
                <Select
                  value={agendaForm.day_layout}
                  label="Layout do dia"
                  onChange={(e) =>
                    setAgendaForm({ ...agendaForm, day_layout: e.target.value as AgendaDefaultDayLayout })}
                >
                  <MenuItem value="professionals">Uma coluna por profissional (padrão)</MenuItem>
                  <MenuItem value="unified">Coluna única</MenuItem>
                  <MenuItem value="list">Lista</MenuItem>
                  <MenuItem value="last">Lembrar o último usado na agenda</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            {showSplitEditHoverPref && (
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={agendaForm.split_edit_hover_expand}
                      onChange={(e) =>
                        setAgendaForm({ ...agendaForm, split_edit_hover_expand: e.target.checked })}
                      color="primary"
                    />
                  }
                  label="Expandir editar consulta ou painel do paciente ao passar o mouse"
                />
                <Typography variant="caption" sx={{ display: 'block', color: '#64748b', ml: 0.5, mt: -0.5 }}>
                  Desligado: os dois painéis ficam abertos lado a lado, sem contrair ao mover o cursor.
                </Typography>
              </Grid>
            )}
          </Grid>
        </Box>

        <UserProfileChangePasswordSection />

        <UserProfileTwoFactorSection
          userName={form.full_name || user.full_name}
          userEmail={user.email}
          twoFactorEnabled={Boolean(user.two_factor_enabled)}
          onStatusChange={refreshUser}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} sx={{ textTransform: 'none' }}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          onClick={() => void handleSave()}
          disabled={saving}
          sx={clinicSettingsPrimaryButtonSx}
        >
          {saving ? <CircularProgress size={18} color="inherit" /> : 'Salvar perfil'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UserProfileDialog;
